import { isEmpty } from 'lodash';
import { useUpdateEffect } from 'react-use';

import {
   HealthcareFacilityLocationSimple,
   HealthcareFacilityLocationSimpleQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { Message, MessageType, Tree } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { healthcareOrganizationTranslationKeys } from '../../../../translations';
import { HealthcareBasicData } from '../../../common/HealthcareBasicData';

type HealthcareFacilityLocationTreeLeavesRenderProps = {
   healthcareFacilityLocations: HealthcareFacilityLocationSimple[];
   refetchHealthcareFacilityLocations: VoidFunction;
};

type HealthcareFacilityLocationTreeLeavesLoaderProps = {
   readonly healthcareParentData: HealthcareBasicData;
   readonly children: (renderProps: HealthcareFacilityLocationTreeLeavesRenderProps) => JSX.Element;
};

export const HealthcareFacilityLocationTreeLeavesLoader = (
   props: HealthcareFacilityLocationTreeLeavesLoaderProps
): JSX.Element => {
   const {
      data: healthcareFacilityLocations,
      loading: healthcareFacilityLocationsLoading,
      error: healthcareFacilityLocationsError,
      refetch: refetchHealthcareFacilityLocations,
   } = useCqrsSearch<HealthcareFacilityLocationSimple>(() => {
      const query = new HealthcareFacilityLocationSimpleQuery();
      query.healthcareFacilityUnitId = Slot.of(props.healthcareParentData.id);
      return query;
   });

   useUpdateEffect(() => {
      refetchHealthcareFacilityLocations();
   }, [props.healthcareParentData]);

   if (healthcareFacilityLocationsLoading) {
      return <Tree.Loader testId={'loader'} />;
   }

   if (healthcareFacilityLocationsError) {
      return (
         <Message
            testId={'failedToFetchHealthcareFacilityLocation'}
            label={healthcareOrganizationTranslationKeys.failedToFetchHealthcareFacilityLocation}
            type={MessageType.NEGATIVE}
            displayIndicator
         />
      );
   }

   if (isEmpty(healthcareFacilityLocations)) {
      return (
         <Tree.Placeholder
            testId={'noData'}
            label={healthcareOrganizationTranslationKeys.noAssignHealthcareFacilityLocation}
         />
      );
   }

   return props.children({
      healthcareFacilityLocations: healthcareFacilityLocations,
      refetchHealthcareFacilityLocations: refetchHealthcareFacilityLocations,
   });
};
